export const COGNITO_CLIENT_ID = '1pnkcpoi3iu4vqfklf94eg6m7i';

// Decodes a display-friendly identity out of a Cognito ID token's JWT
// payload, for display purposes only (e.g. "signed in as..."). This does
// NOT verify the token's signature - that's already handled server-side
// by API Gateway/Lambda on every actual request, so this is purely
// cosmetic and safe to use for UI display without re-implementing
// verification. Prefers the "name" attribute (e.g. "Mike Elliott");
// falls back to email if a user hasn't had a name set yet, so this
// still works gracefully for any account created before this feature.
export function getUserDisplayName(idToken) {
  if (!idToken) return null;

  try {
    const payloadBase64 = idToken.split('.')[1];
    // JWTs use base64url encoding, not plain base64 - swap the two
    // characters that differ before atob() can decode it.
    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return payload.name || payload.email || null;
  } catch (err) {
    return null;
  }
}

// Returns the raw email specifically - used as a stable per-user identity
// key (e.g. for namespacing localStorage entries), as opposed to
// getUserDisplayName above, which is for showing the friendliest string.
export function getUserEmail(idToken) {
  if (!idToken) return null;

  try {
    const payloadBase64 = idToken.split('.')[1];
    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return payload.email || null;
  } catch (err) {
    return null;
  }
}

export async function refreshToken() {
  const storedRefreshToken = sessionStorage.getItem('refreshToken');
  if (!storedRefreshToken) return null;

  try {
    const response = await fetch(
      'https://cognito-idp.eu-west-1.amazonaws.com/',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: COGNITO_CLIENT_ID,
          AuthParameters: {
            REFRESH_TOKEN: storedRefreshToken
          }
        })
      }
    );

    const data = await response.json();

    if (data.AuthenticationResult?.IdToken) {
      const newIdToken = data.AuthenticationResult.IdToken;
      sessionStorage.setItem('idToken', newIdToken);
      return newIdToken;
    }

    return null;
  } catch (err) {
    return null;
  }
}

export async function apiFetch(url, options = {}, onLogout) {
  const token = sessionStorage.getItem('idToken');

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }
  });

  if (response.status === 401) {
    // Try to refresh the token
    const newToken = await refreshToken();

    if (newToken) {
      // Retry the request with the new token
      const retryResponse = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`
        }
      });

      if (retryResponse.status === 401) {
        // Refresh didn't help, log out
        onLogout();
        return null;
      }

      return retryResponse;
    } else {
      // No refresh token available, log out
      onLogout();
      return null;
    }
  }

  return response;
}